import { memo, SetStateAction, useEffect, useState } from 'react';
import { Divider } from 'primereact/divider';
import { useDebounce } from 'primereact/hooks';
import { useWindowSize } from 'react-use';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AlertVariant, MOBILE_BREAKPOINT } from '../../../../../constants/constants';
import { _ToastMessageProps, _Venue } from '../../../../../constants/staticTypes';
import MlxSearch from '../../../../../components/common/MlxSearch/MlxSearch';
import withSidebar from '../../../../../hoc/withSideBar/withSideBar';
import logger from '../../../../../utils/logger';
import { getVenueList } from '../../../../../helpers/venueHelper';
import { COPY_MENU_ITEMS_FROM, COPY_MENU_ITEMS_TO, MENU_LIST, MENU_MANAGEMENT, SELECT_VENUE } from '../../../../../constants/strings';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { VENUE_LIST_MENU, VENUE_MENU_COPY_FROM } from '../../../../../constants/queryKeys';
import MlxDropDown from '../../../../../components/common/MlxDropDown/MlxDropDown';
import { TreeNode } from 'primereact/treenode';
import { TreeTable, TreeTableSelectionKeysType } from 'primereact/treetable';
import { Column } from 'primereact/column';
import MlxBlockUI from '../../../../../components/common/MlxBlockUI/MlxBlockUI';
import { Checkbox } from 'primereact/checkbox';
import MlxButton from '../../../../../components/common/MlxButton/MlxButton';
import {
    _BulkCopyPayloadType,
    buildMenuPayload,
    bulkCopyMenuItems,
    getVenueFbMenu,
    transformMenuDataToTreeTable,
} from '../../../../../helpers/venueMenuHelper';
import MlxConfirmPopup from '../../../../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import HttpServiceHelper from '../../../../../helpers/httpServiceHelper';
import { defaultToastData } from '../../../../../helpers/commonHelper';
import MlxToast from '../../../../../components/common/MlxToast/MlxToast';
import MlxDialog from '../../../../../components/common/MlxDialog/MlxDialog';

const languageTexts = {
    MENU_MANAGEMENT: MENU_MANAGEMENT,
    MENU_LIST: MENU_LIST,
    SELECT_VENUE: SELECT_VENUE,
    COPY_MENU_ITEMS_TO: COPY_MENU_ITEMS_TO,
    COPY_MENU_ITEMS_FROM: COPY_MENU_ITEMS_FROM,
};

type Props = {
    visible: boolean;
    setVisible: React.Dispatch<SetStateAction<boolean>>;
    selectedVenueOption?: string; // the current venue
    selectedCopyVenueOption?: string; // the venue from which the menu is to be copied
    onCopyFromSuccess: () => void;
    venueOptions?: any[];
    setToastMessage: React.Dispatch<SetStateAction<_ToastMessageProps>>;
};

function CopyFromVenueModal({
    visible,
    setVisible,
    selectedVenueOption,
    selectedCopyVenueOption,
    onCopyFromSuccess,
    venueOptions,
    setToastMessage,
}: Readonly<Props>) {
    const { width: screenWidth } = useWindowSize();
    const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce('', 0);
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'none');
    const menuBulkCopyMutation = useMutation(HttpServiceHelper);

    const [searchBarVisible, setSearchBarVisible] = useState(screenWidth > MOBILE_BREAKPOINT);
    const [nodes, setNodes] = useState<TreeNode[]>([]);
    const [selectedNodeKeys, setSelectedNodeKeys] = useState<TreeTableSelectionKeysType>({});
    const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>({});
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [isCopyInProgress, setIsCopyInProgress] = useState(false);

    const copyFromVenueFbMenuQuery = useQuery({
        queryKey: VENUE_MENU_COPY_FROM,
        queryFn: () => getVenueFbMenu(queryClient, selectedCopyVenueOption),
        onSuccess: (response) => {
            const responseData = response?.data?.data;
            const transformedData = transformMenuDataToTreeTable(responseData); // get the data in the required tree format
            setNodes(transformedData);
        },
        onError: (error) => logger.log('Failed to fetch admin staff list', error),
        refetchOnWindowFocus: false,
        enabled: !!selectedCopyVenueOption,
    });

    function findAndExpandMatchingNodes(
        nodes: TreeNode[],
        searchTerm: string
    ): { matchedKeys: string[]; expanded: { [key: string]: boolean } } {
        const matchedKeys: string[] = [];
        const expanded: { [key: string]: boolean } = {};

        function search(nodeList: TreeNode[], parentKeyPath: string[] = []) {
            for (const node of nodeList) {
                const label = String(node.label ?? '').toLowerCase();
                const matches = label.includes(searchTerm.toLowerCase());

                const currentPath = [...parentKeyPath];

                if (matches) {
                    matchedKeys.push(node.key as string);
                    // Expand all ancestors
                    for (const key of currentPath) {
                        expanded[key] = true;
                    }
                }

                if (node.children?.length) {
                    search(node.children, [...currentPath, node.key as string]);
                }
            }
        }

        search(nodes);
        return { matchedKeys, expanded };
    }

    useEffect(() => {
        if (!debouncedSearchValue?.trim()) {
            setExpandedKeys({}); // Collapse all if search is empty
            return;
        }

        const { expanded } = findAndExpandMatchingNodes(nodes, debouncedSearchValue);

        setExpandedKeys(expanded);
    }, [debouncedSearchValue, nodes]);

    const getAllNodeKeys = (nodes: TreeNode[]): TreeTableSelectionKeysType => {
        const keys: TreeTableSelectionKeysType = {};

        const traverse = (nodeList: TreeNode[]) => {
            for (const node of nodeList) {
                keys[node.key as string] = { checked: true, partialChecked: false };
                if (node.children) {
                    traverse(node.children);
                }
            }
        };

        traverse(nodes);
        return keys;
    };

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedNodeKeys({});
        } else {
            const allKeys = getAllNodeKeys(nodes);
            setSelectedNodeKeys(allKeys);
        }
        setIsAllSelected((prev) => !prev);
    };

    const handleBulkCopy = () => {
        const menuPayload = buildMenuPayload(nodes, selectedNodeKeys);

        const copyMenuPayload: _BulkCopyPayloadType = {
            tenantId: selectedCopyVenueOption ?? '',
            newTenantId: selectedVenueOption ?? '',
            menus: menuPayload.menus,
        };

        setIsCopyInProgress(true);

        bulkCopyMenuItems(
            menuBulkCopyMutation,
            copyMenuPayload,
            (data: any) => {
                setToastMessage({
                    message: data?.message ?? '',
                    variant: AlertVariant.SUCCESS,
                    show: true,
                });
                setIsCopyInProgress(false);
                onCopyFromSuccess();
            },
            (error: string, variant: string) => {
                setToastMessage({
                    message: error,
                    variant,
                    show: true,
                });
                setIsCopyInProgress(false);
            }
        );
    };

    const footerContent = (
        <div className="mt-2">
            <MlxButton label="Cancel" icon="pi pi-times" onClick={() => setVisible(false)} className="p-button-text" />
            <MlxButton
                label="Proceed"
                icon="pi pi-check"
                onClick={() => {
                    handleBulkCopy();
                }}
                autoFocus
                disabled={!Object.keys(selectedNodeKeys).length}
            />
        </div>
    );

    return (
        <MlxDialog
            visible={visible}
            onHide={() => setVisible(false)}
            // header={header}
            footer={footerContent}
            style={{ width: '90%', height: '90%' }}
        >
            <MlxBlockUI isLoading={copyFromVenueFbMenuQuery.isFetching || isCopyInProgress} opacity={0.5}>
                <div className="page-section h-100">
                    <div className="page-content h-100">
                        <div className="content-header flex-grow-0">
                            <div>
                                <span>{translatedText.COPY_MENU_ITEMS_FROM}</span>
                                <span className="header-title">{` ${venueOptions?.find((option) => option.id === selectedCopyVenueOption)?.venueName}`}</span>{' '}
                                to
                                <span className="header-title">{` ${venueOptions?.find((option) => option.id === selectedVenueOption)?.venueName}`}</span>
                            </div>
                            <div className="header-fields">
                                <MlxSearch
                                    className="ms-2 p-inputtext-sm"
                                    removeLabelOnMobile={true}
                                    placeholder="Search"
                                    value={searchValue}
                                    onChange={(event) => setSearchValue(event.target.value)}
                                    searchBarVisible={setSearchBarVisible}
                                    isLoading={copyFromVenueFbMenuQuery?.isFetching && !!debouncedSearchValue.trim().length}
                                />
                            </div>
                        </div>
                        <Divider className="content-divider" />
                        <div className="content-body flex-grow-1">
                            <TreeTable
                                className="custom-p-treetable"
                                value={nodes}
                                selectionMode="checkbox"
                                selectionKeys={selectedNodeKeys}
                                onSelectionChange={(e) => setSelectedNodeKeys(e.value as TreeTableSelectionKeysType)}
                                expandedKeys={expandedKeys}
                                onToggle={(e) => setExpandedKeys(e.value)}
                                tableStyle={{ minWidth: '50rem' }}
                                scrollable
                                pt={{
                                    scrollableBody: {
                                        style: {
                                            maxHeight: 'calc(100vh - 24rem)',
                                        },
                                    },
                                }}
                            >
                                <Column
                                    header={
                                        <span>
                                            <Checkbox
                                                checked={isAllSelected}
                                                onChange={toggleSelectAll}
                                                style={{ marginRight: '0.5rem' }}
                                            ></Checkbox>
                                            Menu Name
                                        </span>
                                    }
                                    expander
                                    body={(node) => {
                                        const label = node.label ?? '';
                                        if (debouncedSearchValue) {
                                            const regex = new RegExp(`(${debouncedSearchValue})`, 'ig');
                                            const parts = label.split(regex);
                                            return (
                                                <>
                                                    {parts.map((part: any, i: any) =>
                                                        part.toLowerCase() === debouncedSearchValue.toLowerCase() ? (
                                                            <span key={i} style={{ backgroundColor: 'yellow' }}>
                                                                {part}
                                                            </span>
                                                        ) : (
                                                            <span key={i}>{part}</span>
                                                        )
                                                    )}
                                                </>
                                            );
                                        }
                                        return label;
                                    }}
                                />
                                <Column
                                    header="Type"
                                    body={(node) => {
                                        const type = node.data?.type;
                                        return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
                                    }}
                                />
                                <Column header="Price" body={(node) => node.data?.itemPrices ?? '-'} />
                            </TreeTable>
                        </div>
                    </div>
                </div>
            </MlxBlockUI>
        </MlxDialog>
    );
}

export default memo(CopyFromVenueModal);
